import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import Header from '../components/Header';
import LeadForm from '../components/LeadForm';
import { Star, ArrowRight, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function SafeHtml({ html }) {
  const clean = DOMPurify.sanitize(html || '');
  return <div className="prose prose-lg font-['IBM_Plex_Sans'] text-gray-700" dangerouslySetInnerHTML={{ __html: clean }} />;
}

function injectTrackingPixel(code, type) {
  if (!code) return;
  const sanitized = DOMPurify.sanitize(code, { ALLOWED_TAGS: ['script', 'noscript', 'img'], ALLOWED_ATTR: ['src', 'async', 'defer', 'id', 'class'] });
  if (!sanitized) return;
  const container = document.createElement('div');
  container.setAttribute('data-pixel-type', type);
  container.innerHTML = sanitized;
  document.head.appendChild(container);
}

export default function FiduciaryLandingPage() {
  const { slug } = useParams();
  const [fiduciary, setFiduciary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchFiduciary = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/public/fiduciary/${slug}`);
      setFiduciary(data);
      document.title = data.meta_title || `${data.name} — Expert fiscal ${data.city}`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc && data.meta_description) metaDesc.setAttribute('content', data.meta_description);
      injectTrackingPixel(data.google_pixel, 'google');
      injectTrackingPixel(data.meta_pixel, 'meta');
    } catch {
      setError('Fiduciaire non trouvé');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { fetchFiduciary(); }, [fetchFiduciary]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="animate-spin text-[#E3000F]" size={32} /></div>;
  }
  if (error || !fiduciary) {
    return <div className="min-h-screen flex items-center justify-center bg-white"><p className="text-gray-600 font-['IBM_Plex_Sans']">{error || 'Page non trouvée'}</p></div>;
  }

  return (
    <div className="min-h-screen bg-white" data-testid="fiduciary-landing-page">
      <Header fiduciaryName={fiduciary.name} fiduciaryPhone={fiduciary.phone} />
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E3000F] mb-4 font-['IBM_Plex_Sans']">{fiduciary.name} — {fiduciary.city}</p>
              <h1 className="font-['Chivo'] font-black text-4xl sm:text-5xl lg:text-6xl text-[#111827] tracking-tight leading-none mb-6">
                {fiduciary.custom_heading || `Votre déclaration d'impôt à ${fiduciary.city}, entre de bonnes mains.`}
              </h1>
              <p className="text-base sm:text-lg text-gray-600 font-['IBM_Plex_Sans'] leading-relaxed mb-8 max-w-2xl">
                {fiduciary.custom_subheading || `Confiez votre déclaration d'impôt 2025 à ${fiduciary.name}. Expert fiscal certifié à ${fiduciary.city}, nous optimisons chaque franc de votre déclaration.`}
              </p>
              <div className="flex items-center gap-4 mb-8">
                <div className="flex gap-1">
                  {[1,2,3,4,5].map((n) => <Star key={`fid-star-${n}`} size={18} className="fill-[#E3000F] text-[#E3000F]" />)}
                </div>
                <span className="text-sm font-semibold text-gray-700 font-['IBM_Plex_Sans']">Expert certifié — {fiduciary.city}</span>
              </div>
              <a href="#formulaire-fiduciaire" className="bg-[#E3000F] text-white hover:bg-[#A6000B] px-8 py-4 font-bold text-base tracking-wide transition-colors font-['IBM_Plex_Sans'] inline-flex items-center gap-2" data-testid="fiduciary-hero-cta">
                Demander un devis gratuit <ArrowRight size={18} />
              </a>
            </div>
            <div className="lg:col-span-5" id="formulaire-fiduciaire">
              <div className="border border-gray-200 bg-[#F9FAFB] p-6 sm:p-8" data-testid="fiduciary-form-container">
                <h2 className="font-['Chivo'] font-bold text-xl sm:text-2xl text-[#111827] mb-1">Devis gratuit — {fiduciary.name}</h2>
                <p className="text-sm text-gray-500 mb-6 font-['IBM_Plex_Sans']">Nous vous recontactons sous 24h</p>
                <LeadForm fiduciarySlug={slug} city={fiduciary.city} />
              </div>
            </div>
          </div>
        </div>
      </section>
      {fiduciary.custom_description && (
        <section className="py-16 bg-[#F9FAFB] border-b border-gray-200">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <SafeHtml html={fiduciary.custom_description} />
          </div>
        </section>
      )}
      <footer className="bg-[#111827] text-gray-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-xs">
          <p>&copy; {new Date().getFullYear()} {fiduciary.name} — Tous droits réservés</p>
        </div>
      </footer>
    </div>
  );
}
